import { isEmpty } from "lodash";
import React from "react";
import PropTypes from "prop-types";
import cx from "classnames";

import Button from "antd/lib/button";
import Tooltip from "@/components/Tooltip";
import routeWithApiKeySession from "@/components/ApplicationArea/routeWithApiKeySession";
import Link from "@/components/Link";
import BigMessage from "@/components/BigMessage";
import PageHeader from "@/components/PageHeader";
import Parameters from "@/components/Parameters";
import DashboardGrid from "@/components/dashboards/DashboardGrid";
import Filters from "@/components/Filters";

import { Dashboard } from "@/services/dashboard";
import routes from "@/services/routes";

import { ThemeProvider, useTheme } from "@/components/ThemeProvider";

import logoUrl from "@/assets/images/redash_icon_small.png";

import useDashboard from "./hooks/useDashboard";

import "./PublicDashboardPage.less";

function DashboardBackgroundLayer({ dashboard }) {
  const bg = dashboard && dashboard.options && dashboard.options.background;
  if (!bg || !bg.image_url) return null;
  const style = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    zIndex: 0,
    pointerEvents: "none",
    backgroundSize: "100% 100%",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundImage: `url(${bg.image_url})`,
    opacity: bg.opacity !== undefined ? bg.opacity : 1.0,
  };
  return <div className="dashboard-bg" style={style} />;
}

function ThemeToggleButton() {
  const { theme, setTheme } = useTheme();

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  return (
    <Tooltip title={theme === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"}>
      <Button
        className="theme-toggle-btn m-l-5"
        onClick={toggleTheme}
        aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      >
        <i
          className={cx("zmdi", {
            "zmdi-brightness-7": theme === "dark",
            "zmdi-brightness-2": theme !== "dark",
          })}
          aria-hidden="true"
        />
      </Button>
    </Tooltip>
  );
}

function PublicDashboard({ dashboard }) {
  const { globalParameters, filters, setFilters, refreshDashboard, loadWidget, refreshWidget } = useDashboard(
    dashboard
  );

  return (
    <div className="container p-t-10 p-b-20">
      <div className="dashboard-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <PageHeader title={dashboard.name} />
        <ThemeToggleButton />
      </div>
      {!isEmpty(globalParameters) && (
        <div className="m-b-10 p-15 bg-white tiled">
          <Parameters parameters={globalParameters} onValuesChange={refreshDashboard} />
        </div>
      )}
      {!isEmpty(filters) && (
        <div className="m-b-10 p-15 bg-white tiled">
          <Filters filters={filters} onChange={setFilters} />
        </div>
      )}
      <div id="dashboard-container" style={{ position: "relative" }}>
        <DashboardBackgroundLayer dashboard={dashboard} />
        <DashboardGrid
          dashboard={dashboard}
          widgets={dashboard.widgets}
          filters={filters}
          isEditing={false}
          isPublic
          onLoadWidget={loadWidget}
          onRefreshWidget={refreshWidget}
        />
      </div>
    </div>
  );
}

PublicDashboard.propTypes = {
  dashboard: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

class PublicDashboardPage extends React.Component {
  static propTypes = {
    token: PropTypes.string.isRequired,
    onError: PropTypes.func,
  };

  static defaultProps = {
    onError: () => {},
  };

  state = {
    loading: true,
    dashboard: null,
  };

  componentDidMount() {
    Dashboard.getByToken({ token: this.props.token })
      .then(dashboard => this.setState({ dashboard, loading: false }))
      .catch(error => this.props.onError(error));
  }

  render() {
    const { loading, dashboard } = this.state;
    return (
      <ThemeProvider>
        <div className="public-dashboard-page">
          {loading ? (
            <div className="container loading-message">
              <BigMessage className="" icon="fa-spinner fa-2x fa-pulse" message="Loading..." />
            </div>
          ) : (
            <PublicDashboard dashboard={dashboard} />
          )}
          <div id="footer">
            <div className="text-center">
              <Link href="https://redash.io">
                <img alt="" src={logoUrl} width="38" />
              </Link>
            </div>
            {/*Powered by <Link href="">Cube</Link>*/}
            Powered by Unisco
          </div>
        </div>
      </ThemeProvider>
    );
  }
}

routes.register(
  "Dashboards.ViewShared",
  routeWithApiKeySession({
    path: "/public/dashboards/:token",
    render: pageProps => <PublicDashboardPage {...pageProps} />,
    getApiKey: currentRoute => currentRoute.routeParams.token,
  })
);


